import React, { useCallback, useMemo, useState } from 'react';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import UserCell from '../../components/ui/UserCell';
import StatusPill from '../../components/ui/StatusPill';
import Button from '../../components/ui/Button';
import Drawer, { DrawerField, DrawerSection } from '../../components/overlays/Drawer';
import Modal from '../../components/overlays/Modal';
import { Field, Select, Textarea, TextInput } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  addNewFollowUpActivity,
  getFollowUpDetailById,
  getFollowUpList,
} from '../../actions/followUpAction';
import { formatDateTime, phone } from '../../helpers/format';
import './operations.css';

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'close', label: 'Close' },
];

/** The follow-up API paginates with page/limit and returns its own envelope shape. */
async function fetchFollowUps(params) {
  const data = await getFollowUpList({ page: params.page, limit: params.limit });
  return { items: data?.follow_ups || [], total: data?.total_records || 0 };
}

export default function OrderFollowUp() {
  const { toast } = useOverlays();
  const [record, setRecord] = useState(null);
  const [logging, setLogging] = useState(false);
  const [status, setStatus] = useState('open');
  const [notes, setNotes] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [saving, setSaving] = useState(false);

  const list = useListState({ views: [], fetcher: fetchFollowUps, limit: 20 });

  const open = useCallback(async (row) => {
    setRecord(row);
    // The list payload omits the linked report/session; the detail endpoint resolves them.
    try {
      const full = await getFollowUpDetailById(row.id);
      if (full?.id) setRecord(full);
    } catch {
      /* keep the list row; it still shows the essentials */
    }
  }, []);

  const save = useCallback(async () => {
    if (status === 'open' && !nextDate) return;
    setSaving(true);
    try {
      await addNewFollowUpActivity(record.id, {
        notes,
        status,
        // The API expects "YYYY-MM-DD HH:mm:ss.SSS ±HHMM".
        next_follow_up_date: nextDate ? `${nextDate} 09:00:00.000 +0000` : null,
      });
      toast('Follow-up updated', { tone: 'success' });
      setLogging(false);
      setNotes('');
      setNextDate('');
      setRecord(null);
      list.reload();
    } catch {
      toast('Could not update the follow-up', { tone: 'error' });
    } finally {
      setSaving(false);
    }
  }, [record, status, notes, nextDate, toast, list]);

  const columns = useMemo(
    () => [
      {
        key: 'user',
        header: 'User',
        width: 250,
        render: (r) => <UserCell name={r.user?.name || '—'} secondary={r.user?.email} seed={r.user?.id} />,
      },
      { key: 'phone', header: 'Phone', width: 160, mono: true, render: (r) => phone(r.user?.phone) },
      { key: 'follow_up_reason', header: 'Reason', muted: true, render: (r) => r.follow_up_reason || '—' },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: 'created_at',
        header: 'Created at',
        width: 180,
        muted: true,
        render: (r) => formatDateTime(r.created_at),
      },
    ],
    []
  );

  const events = record?.follow_up_events || [];
  const products = record?.product_info || {};
  const isClosed = String(record?.status || '').toLowerCase() === 'close';

  return (
    <>
      <PageHeader
        section="Operations"
        title="Order follow-up"
        subtitle="Orders where payment succeeded but fulfilment did not."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="follow-ups"
        selectable={false}
        searchPlaceholder="User name or email"
        onRowClick={open}
        empty={{ icon: 'bx-phone-call', title: 'No follow-ups', description: 'Nothing needs chasing.' }}
      />

      <Drawer
        open={!!record}
        onClose={() => setRecord(null)}
        title={record?.user?.name || 'Follow-up'}
        status={record?.status}
        footer={
          !isClosed ? (
            <Button variant="primary" onClick={() => setLogging(true)}>
              Log activity
            </Button>
          ) : null
        }
      >
        <DrawerSection title="User">
          <DrawerField label="Name">{record?.user?.name}</DrawerField>
          <DrawerField label="Email">{record?.user?.email}</DrawerField>
          <DrawerField label="Phone" mono>
            {phone(record?.user?.phone)}
          </DrawerField>
          <DrawerField label="Assigned to">{record?.admin?.name}</DrawerField>
        </DrawerSection>

        <DrawerSection title="Reason">
          <DrawerField label="Reason">{record?.follow_up_reason}</DrawerField>
          <DrawerField label="Created">{formatDateTime(record?.created_at)}</DrawerField>
          <DrawerField label="Next follow-up">{formatDateTime(record?.next_follow_up_date)}</DrawerField>
        </DrawerSection>

        {(products.reports?.length > 0 || products.sessions?.length > 0) && (
          <DrawerSection title="Linked orders">
            {products.reports?.map((r) => (
              <DrawerField key={r.id} label="Report">
                {r.report_type} — {r.latest_status}
              </DrawerField>
            ))}
            {products.sessions?.map((s) => (
              <DrawerField key={s.id} label="Session">
                {s.agent_service?.title} — {s.status}
              </DrawerField>
            ))}
          </DrawerSection>
        )}

        <DrawerSection title={`Activity (${events.length})`}>
          {events.length === 0 && (
            <div className="sv-drawer-field__label" style={{ padding: '8px 0' }}>
              No activity logged yet.
            </div>
          )}
          {events.map((event, i) => (
            <div key={i} className="sv-followup-event">
              <div className="sv-followup-event__title">{event.title}</div>
              {event.notes && <div className="sv-followup-event__notes">{event.notes}</div>}
              <div className="sv-followup-event__meta">
                {event.created_by?.name} · {formatDateTime(event.created_at)}
              </div>
            </div>
          ))}
        </DrawerSection>
      </Drawer>

      <Modal
        open={logging}
        onClose={() => setLogging(false)}
        title="Log follow-up activity"
        size="form"
        confirmLabel="Save"
        confirmLoading={saving}
        confirmDisabled={status === 'open' && !nextDate}
        onConfirm={save}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <Field label="Status" required>
            <Select options={STATUSES} value={status} onChange={(e) => setStatus(e.target.value)} />
          </Field>
          <Field
            label="Next follow-up date"
            required={status === 'open'}
            hint={status === 'open' ? 'Required while the follow-up stays open' : undefined}
          >
            <TextInput type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
          </Field>
          <Field label="Notes">
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        {status === 'close' && (
          <p style={{ marginBottom: 0 }}>
            Closing a follow-up is final — it cannot be reopened or edited afterwards.
          </p>
        )}
      </Modal>
    </>
  );
}
